import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageMarker from 'react-image-marker';

import ComingSoonImg from '../../../../assets/dynamic-colors/CommingSvg';
import NoDataSVG from '../../../../assets/dynamic-colors/No-DataSVG';
import { ReactComponent as CarIcon } from '../../../../assets/icons/black-car-icon.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
// import ComingSoonImages from '../../../../assets/icons/coming-soon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit-pencil-card.svg';
import PumpIcon from '../../../../assets/icons/gas-icon.svg';
import { ReactComponent as ImageBreakIcon } from '../../../../assets/icons/image-break-icon.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/plus.svg';
import LaneImage from '../../../../assets/images/lane.png';
// import NoDataFound from '../../../assets/images/no-data-found.png';
import { brandIcons } from '../../../../config/branding';
import { useGoogleMaps } from '../../../../contexts/google-map-context/GoogleMapContent';
import { LaneData, MarkerWithDevice } from '../../../../interface/lane-types-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import stationManagement from '../../../../utils/api/station-management';

import AddLaneStation from './AddLaneStation';
import EditLaneStation from './EditLaneStation';

interface ChargerStatus {
  status: string;
  count: number;
}

interface Device {
  id: string;
  name: string;
  status: 'available' | 'maintenance' | 'error' | 'unavailable';
  connectorType?: string;
  connectors?: any[];
  latitude?: number;
  longitude?: number;
  chargingPointSn: string;
}

/**
 * check the lat and lng is valid or not
 * @param {any} position - The position
 * @param {any} position.latitude - The latitude prop
 * @param {any} position.longitude - The longitude prop
 */
const validatePosition = (position: { latitude: any; longitude: any }) => {
  const lat = parseFloat(position.latitude);
  const lng = parseFloat(position.longitude);
  return !Number.isNaN(lat) && !Number.isNaN(lng) ? { lat, lng } : null;
};

/**
 * Check the Image Url for valid
 * @param {any} url The URL to validate
 * @returns {any} True if valid URL, false otherwise
 */
const isValidUrl = (url?: string): boolean => {
  if (!url) return false;
  try {
    return Boolean(new URL(url));
  } catch {
    return false;
  }
};

/**
 * Prevent image drag
 * @param {any} event - The drag event
 */
const handlePreventDrag = (event: React.DragEvent<HTMLImageElement>) => {
  event.preventDefault();
};

/**
 * Station Devices Lane
 */
function StationDevicesLane() {
  const { isLoaded } = useGoogleMaps();
  const mapReference = useRef<google.maps.Map | null>(null);
  const [activeTab, setActiveTab] = useState<string>('mapLane');
  const [openTab, setOpenTab] = useState<number>(0);
  const [devices, setDevices] = useState<Device[]>([]);
  const dispatch = useAppDispatch();
  const [chargerStatus, setChargerStatus] = useState<ChargerStatus[]>([]);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [lanes, setLanes] = useState<LaneData[]>([]);
  const [selectedLaneId, setSelectedLaneId] = useState<string | null>(null);
  const selectedLane = lanes.find((l) => l.id === selectedLaneId) || lanes[0] || null;
  const { stationDetails } = useAppSelector((state) => state.stationDetails);
  const [loading, setLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { t, i18n } = useTranslation('sidePanel');

  const [dynamicIconUrl, setDynamicIconUrl] = useState<string>(brandIcons.stationLocator);

  useEffect(() => {
    /**
     * Function to fetch and colorize the icon
     */
    const fetchAndColorizeIcon = async () => {
      try {
        const response = await fetch(brandIcons.stationLocator);
        const text = await response.text();
        const primaryColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--primary-color')
          .trim();

        // Replace currentColor with the actual primary color value
        // Since the user set fill="currentColor", this replacement makes it static for the marker image.
        const coloredSvg = text.replace(/currentColor/g, primaryColor || '#00A79D');

        // Convert to data URI
        const encoded = encodeURIComponent(coloredSvg);
        const dataUri = `data:image/svg+xml;charset=utf-8,${encoded}`;
        setDynamicIconUrl(dataUri);
      } catch (error) {
        // console.error('Error processing station icon:', error);
        // Fallback to original URL if fetch or processing fails
      }
    };

    fetchAndColorizeIcon();
  }, [brandIcons.stationLocator]);

  /**
   * Fetch device list
   */
  const handleDevicesList = async () => {
    if (!selectedChargingPoint) return;

    try {
      const response = await stationManagement.fetchDeviceList({
        chargingStation: selectedChargingPoint?.id,
      });

      const formattedDevices: Device[] = response.data.map((device: any) => ({
        id: device._id,
        name: device.chargingPointName || t('empty'),
        chargingPointSn: device.chargingPointSn,
        status: device.isDeviceOffline ? 'unavailable' : 'available',
        connectorType: device.connectorType,
        connectors: device.connectors,
      }));

      setDevices(formattedDevices);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  /**
   * Fetch lane map list
   * @param {any} selectLaneName - The selectLaneName
   */
  const handleLaneMapList = async (selectLaneName?: string) => {
    if (!selectedChargingPoint?.id) return;
    setLoading(true);
    try {
      const response = await stationManagement.fetchLaneMapList({
        chargingStation: selectedChargingPoint?.id,
      });
      const dataArray = Array.isArray(response.data) ? response.data : [response.data];

      const fetchedLanes: LaneData[] = [];
      dataArray.forEach((item: any) => {
        if (item && Array.isArray(item.chargingStationMaps)) {
          item.chargingStationMaps.forEach((mapItem: any) => {
            fetchedLanes.push({
              id: mapItem._id,
              name: mapItem.name || `Default Lane`,
              siteMapUrl: mapItem.signedUrl || mapItem.url || '',
              markers: (mapItem.chargingPointLocations || []).map((loc: any) => ({
                top: loc.position?.top || 0,
                left: loc.position?.left || 0,
                device: loc.chargingPointName || loc.chargingPointSn || '',
                chargingPointSn: loc.chargingPointSn || '',
                position: { top: loc.position?.top || 0, left: loc.position?.left || 0 },
              })),
            });
          });
        }
      });

      setLanes(fetchedLanes);
      if (fetchedLanes.length > 0) {
        if (selectLaneName) {
          const match = fetchedLanes.find((l) => l.name === selectLaneName);
          if (match) {
            setSelectedLaneId(match.id);
            return;
          }
        }
        setSelectedLaneId((previousId) => {
          if (previousId && fetchedLanes.some((l) => l.id === previousId)) {
            return previousId;
          }
          return fetchedLanes[0].id;
        });
      } else {
        setSelectedLaneId(null);
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLanes([]);
    setSelectedLaneId(null);
    if (selectedChargingPoint?.id) {
      handleDevicesList();
      handleLaneMapList();
    }
  }, [selectedChargingPoint]);

  /**
   * active tab section
   * @param {any} number The index of the tab to open
   */
  const changeTabPages = (number: number) => {
    setOpenTab(number);
  };

  /**
   * Connector Status
   */
  const handleConnectorStatus = async () => {
    try {
      const response = await stationManagement.fetchConnectorStatus({
        chargingStation: selectedChargingPoint?.id,
      });
      setChargerStatus(response?.data?.statusCounts);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  useEffect(() => {
    handleConnectorStatus();
  }, [selectedChargingPoint]);
  /**
   * Clean Device Name
   * @param {any} marker - The marker
   */
  const getCleanDeviceName = (marker: MarkerWithDevice) => {
    const found = devices.find((d) => d.chargingPointSn === marker.chargingPointSn);
    if (found) return found.name;
    return marker.device.split('(')[0].trim() || marker.device;
  };

  /**
   * Save a new lane
   * @param {any} newLane - The newLane
   * @param {any} newLane.id - The id prop
   * @param {any} newLane.name - The name prop
   * @param {any} newLane.siteMapUrl - The siteMapUrl prop
   * @param {any} newLane.markers - The markers prop
   * @param {any} rawFile - The rawFile
   */
  const handleSaveNewLane = async (
    newLane: { id: string; name: string; siteMapUrl: string; markers: MarkerWithDevice[] },
    rawFile: File | null,
  ) => {
    if (!selectedChargingPoint?.id) return;

    try {
      const formData = new FormData();
      if (rawFile) {
        formData.append('chargingStationMap', rawFile);
      }
      formData.append('names[0]', newLane.name);

      newLane.markers.forEach((marker, index) => {
        const cleanName = getCleanDeviceName(marker);
        formData.append(`chargingPointLocationsByMap[0][${index}][chargingPointName]`, cleanName);
        formData.append(
          `chargingPointLocationsByMap[0][${index}][chargingPointSn]`,
          marker.chargingPointSn,
        );
        formData.append(
          `chargingPointLocationsByMap[0][${index}][position][top]`,
          marker.position.top.toString(),
        );
        formData.append(
          `chargingPointLocationsByMap[0][${index}][position][left]`,
          marker.position.left.toString(),
        );
      });

      await stationManagement.addLaneMap(selectedChargingPoint.id, formData);

      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'success',
          notificationDetails: {
            statusCode: 200,
            message: t('laneSavedSuccessfully') || 'Lane saved successfully!',
          },
        },
      });

      await handleLaneMapList(newLane.name);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.status === 413
              ? 'Image size is too large!'
              : error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
      throw error;
    }
  };

  /**
   * Update an existing lane
   * @param {any} updatedLane - The updatedLane
   * @param {any} updatedLane.id - The id prop
   * @param {any} updatedLane.name - The name prop
   * @param {any} updatedLane.siteMapUrl - The siteMapUrl prop
   * @param {any} updatedLane.markers - The markers prop
   * @param {any} rawFile - The rawFile
   */
  const handleUpdateLane = async (
    updatedLane: { id: string; name: string; siteMapUrl: string; markers: MarkerWithDevice[] },
    rawFile: File | null,
  ) => {
    if (!selectedChargingPoint?.id) return;

    try {
      const formData = new FormData();
      formData.append('name', updatedLane.name);
      if (rawFile) {
        formData.append('chargingStationMap', rawFile);
      }
      updatedLane.markers.forEach((marker, index) => {
        const cleanName = getCleanDeviceName(marker);
        formData.append(`chargingPointLocations[${index}][chargingPointName]`, cleanName);
        formData.append(
          `chargingPointLocations[${index}][chargingPointSn]`,
          marker.chargingPointSn,
        );
        formData.append(
          `chargingPointLocations[${index}][position][top]`,
          marker.position.top.toString(),
        );
        formData.append(
          `chargingPointLocations[${index}][position][left]`,
          marker.position.left.toString(),
        );
      });

      await stationManagement.editLaneMap(selectedChargingPoint.id, updatedLane.id, formData);

      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'success',
          notificationDetails: {
            statusCode: 200,
            message: t('laneUpdatedSuccessfully') || 'Lane updated successfully!',
          },
        },
      });

      await handleLaneMapList(updatedLane.name);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message:
            error?.response?.status === 413
              ? 'Image size is too large!'
              : error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
      throw error;
    }
  };

  /**
   * Render Lane Toggle Button
   */
  const renderLaneToggleButton = () => {
    switch (activeTab) {
      // case 'mapLane':
      //   return (
      //     <div
      //       className="add-lane"
      //       role="button"
      //       tabIndex={0}
      //       onClick={() => setActiveTab('defaultLane')}
      //       onKeyDown={(event) => {
      //         if (event.key === 'Enter' || event.key === ' ') setActiveTab('defaultLane');
      //       }}>
      //       <span>{t('defaultLane')}</span>
      //     </div>
      //   );

      case 'defaultLane':
        return (
          <div
            className="add-lane"
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('mapLane')}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') setActiveTab('mapLane');
            }}>
            <span>{t('mapLane')}</span>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="station-devices-lane-content" dir={i18n.dir()}>
      <div className="station-heading">
        <div className="table-tab">
          {openTab === 12345678 && (
            <CustomToggleButton
              defaultActiveIndex={openTab}
              onToggle={changeTabPages}
              labels={[t('lane')]}
            />
          )}
        </div>

        <div className="station-heading-actions">
          {openTab !== 1 && openTab !== 2 && lanes.length > 0 && renderLaneToggleButton()}
          {(permissionCheck('partnerChargingStationAdd') ||
            permissionCheck('cpoChargingStationAdd')) && (
              <div
                className={`add-lane ${activeTab === 'addLane' ? 'active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setActiveTab('addLane')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveTab('addLane');
                  }
                }}>
                <PlusIcon className="add-lane-icon" /> <span>{t('addLane')}</span>
              </div>
            )}
          {lanes.length > 0 &&
            (permissionCheck('partnerChargingStationUpdate') ||
              permissionCheck('cpoChargingStationUpdate')) && (
              <div
                className={`add-lane ${activeTab === 'editLane' ? 'active' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => setActiveTab('editLane')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    setActiveTab('editLane');
                  }
                }}>
                <EditIcon className="edit-lane-icon" />
                <span>{t('editLane')}</span>
              </div>
            )}
        </div>
      </div>

      {openTab === 0 && (
        <div className="lane-layout-container">
          <div className="lane-sidebar">
            <div>
              <div className="lane-list">
                {lanes.length > 0 ? (
                  lanes.map((lane) => (
                    <div
                      key={lane.id}
                      className={`lane-list-item ${selectedLane?.id === lane.id ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedLaneId(lane.id);
                        setActiveTab('mapLane');
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          setSelectedLaneId(lane.id);
                          setActiveTab('mapLane');
                        }
                      }}>
                      <span className="lane-item-name" title={lane.name}>
                        {lane.name}
                      </span>
                    </div>
                  ))
                ) : (
                  // <div
                  //   className="lane-list-item active"
                  //   onClick={() => {
                  //     setActiveTab('mapLane');
                  //   }}
                  //   role="button"
                  //   tabIndex={0}
                  //   onKeyDown={(event) => {
                  //     if (event.key === 'Enter' || event.key === ' ') {
                  //       setActiveTab('mapLane');
                  //     }
                  //   }}>
                  //   <span className="lane-item-name" title={t('defaultLane') || 'Default Lane'}>
                  //     {t('defaultLane') || 'Default Lane'}
                  //   </span>
                  // </div>
                  <div> </div>
                )}
              </div>
            </div>
          </div>

          <div className="lane-viewer">
            {loading ? (
              <div className="skeleton-grid-loader">
                <div className="skeleton-card" />
              </div>
            ) : (
              <>
                {(activeTab === 'mapLane' ||
                  activeTab === 'addLane' ||
                  activeTab === 'editLane') && (
                    <div className="lane-display">
                      <div>
                        {selectedLane?.siteMapUrl && (
                          <img
                            src={selectedLane.siteMapUrl}
                            alt="preload"
                            className="lane-preload-img"
                            onLoad={() => setImageLoaded(true)}
                            onError={() => setImageLoaded(false)}
                          />
                        )}
                        {!imageLoaded && selectedLane?.siteMapUrl && (
                          <div className="error-message">
                            <ImageBreakIcon />
                            <span>{t('laneMapImageError')}</span>
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => setImageLoaded(false)}
                              onKeyDown={(event_) => {
                                if (event_.key === 'Enter' || event_.key === ' ') {
                                  setImageLoaded(false);
                                }
                              }}>
                              <CloseIcon />
                            </div>
                          </div>
                        )}
                        <ImageMarker
                          src={
                            selectedLane && isValidUrl(selectedLane.siteMapUrl) && imageLoaded
                              ? selectedLane.siteMapUrl
                              : LaneImage
                          }
                          markers={selectedLane ? selectedLane.markers : []}
                          markerComponent={(marker) => {
                            const m = marker as unknown as MarkerWithDevice;
                            return (
                              <div className="marker-wrapper" draggable={false}>
                                <div className="device-marker" draggable={false}>
                                  <img
                                    src={PumpIcon}
                                    alt={t('pump')}
                                    className="device-details-image"
                                    draggable={false}
                                    onDragStart={handlePreventDrag}
                                  />
                                  <p className="marker-device-label">
                                    {devices.find((d) => d.chargingPointSn === m.chargingPointSn)
                                      ?.name || m.device}
                                  </p>
                                </div>
                                <div className="device-status-container online">
                                  <div className="static-container" />
                                  <div className="animation-container" />
                                </div>
                              </div>
                            );
                          }}
                        />
                      </div>
                    </div>
                  )}
                {activeTab === 'defaultLane' && (
                  <div className="device-page-car">
                    {devices.length > 0 ? (
                      <div className="device-grid">
                        {devices.map((device) => (
                          <div key={device.id} className={`device-card ${device.status}`}>
                            {device.status === 'available' && (
                              <div className="car-image">
                                <CarIcon />
                              </div>
                            )}
                            <div className="device-info">
                              {device.status === 'maintenance' && (
                                <p className="status-text">{t('maintenance')}</p>
                              )}
                              {device.status === 'error' && (
                                <p className="status-text">{t('error')}</p>
                              )}
                              {device.status === 'unavailable' && (
                                <p className="status-text unavailable-text">{t('unavailable')}</p>
                              )}
                              <div
                                className={`device-name ${device.name.length > 25 ? 'long' : ''}`}>
                                {device.name}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="table-no-data-wrap">
                        <NoDataSVG primaryColor="var(--primary-color)" />
                        <div className="no-data-found-details">
                          <h3>{t('noDataTitle')}</h3>
                          <p>{t('noDataDescription')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {chargerStatus.length === 121311 && (
              <div className="devices-status-label">
                <div className="each-label">
                  <p>{t('available')}</p>
                  <div className="each-label-color bg-available">
                    {chargerStatus.find((s) => s.status === 'Available')?.count || 0}
                  </div>
                </div>
                <div className="each-label">
                  <p>{t('charging')}</p>
                  <div className="each-label-color bg-charging">
                    {chargerStatus.find((s) => s.status === 'Charging')?.count || 0}
                  </div>
                </div>
                <div className="each-label">
                  <p>{t('finishing')}</p>
                  <div className="each-label-color bg-finishing">
                    {chargerStatus.find((s) => s.status === 'Finishing')?.count || 0}
                  </div>
                </div>
                <div className="each-label">
                  <p>{t('preparing')}</p>
                  <div className="each-label-color bg-preparing">
                    {chargerStatus.find((s) => s.status === 'Preparing')?.count || 0}
                  </div>
                </div>
                <div className="each-label">
                  <p>{t('Faulted')}</p>
                  <div className="each-label-color bg-faulted">
                    {chargerStatus.find((s) => s.status === 'Faulted')?.count || 0}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CCTV Tab */}
      {openTab === 1 && (
        <div className="cctv-container">
          <center>
            {/* <img src={ComingSoonImages} alt={t('comingSoon')} /> */}
            <ComingSoonImg primaryColor="var(--primary-color)" />
          </center>
        </div>
      )}

      {/* Map Tab */}
      {openTab === 2 && (
        <div className="map-container">
          <div className="map">
            {isLoaded && stationDetails ? (
              <GoogleMap
                mapContainerStyle={{
                  width: '100%',
                  height: '100%',
                }}
                center={{
                  lat: Number(stationDetails?.latitude),
                  lng: Number(stationDetails?.longitude),
                }}
                zoom={3}
                onLoad={(map) => {
                  mapReference.current = map; // Assign the map instance to the reference
                }}>
                {stationDetails &&
                  (() => {
                    const validPosition = validatePosition({
                      latitude: stationDetails.latitude,
                      longitude: stationDetails.longitude,
                    });
                    return validPosition ? (
                      <Marker
                        key="01"
                        position={validPosition}
                        // clusterer={clusterer}
                        icon={{
                          url: dynamicIconUrl,
                          scaledSize: new window.google.maps.Size(44, 44), // Width, Height
                        }}
                      />
                    ) : null;
                  })()}
              </GoogleMap>
            ) : null}
          </div>

          <div className="available-status-label">
            <div className="each-label">
              <div className="each-label-color bg-green" />
              <p>{t('available')}</p>
            </div>
            <div className="each-label">
              <div className="each-label-color bg-gray" />
              <p>{t('unavailable')}</p>
            </div>
            <div className="each-label">
              <div className="each-label-color bg-orange" />
              <p>{t('maintenance')}</p>
            </div>
          </div>
        </div>
      )}


      {/* Add/Edit Lane Modals */}
      <ModalBox
        open={activeTab === 'addLane'}
        title={null}
        header={false}
        notification
        closeModal={() => {
          setActiveTab('mapLane');
        }}
        content={
          <AddLaneStation
            onClose={() => {
              setActiveTab('mapLane');
            }}
            onSaveLane={handleSaveNewLane}
            assignedDevices={lanes.flatMap((l) => l.markers.map((m) => m.chargingPointSn))}
          />
        }
      />

      <ModalBox
        open={activeTab === 'editLane' && selectedLane !== null}
        title={null}
        header={false}
        notification
        closeModal={() => {
          setActiveTab('mapLane');
        }}
        content={
          selectedLane ? (
            <EditLaneStation
              onClose={() => {
                setActiveTab('mapLane');
              }}
              initialLaneId={selectedLane.id}
              initialLaneName={selectedLane.name}
              initialPreview={selectedLane.siteMapUrl}
              initialMarkers={selectedLane.markers}
              onSaveLane={handleUpdateLane}
              assignedDevices={lanes
                .filter((l) => l.id !== selectedLane.id)
                .flatMap((l) => l.markers.map((m) => m.chargingPointSn))}
            />
          ) : null
        }
      />
    </div>
  );
}

export default StationDevicesLane;
